import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIdentity } from '@gov-nx/auth/common';
import { MyAuthorizationsContentView } from '@gov-nx/component/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { is } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import {
	MojeAutorizaceSeznamContextProvider,
	MojeOsvedceniSeznamContextProvider,
	MojePodaniSeznamContextProvider,
	useMySubmissionTabs,
	MySubmissionTabs,
} from '@gov-nx/module/page';
import { Tabs } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { MyVerificationsList } from '../../moje-osvedceni/seznam/MyVerificationsList';
import { MySubmissionsList } from '../seznam/MySubmissionsList';

export interface MyVerificationsTabsViewProps {
	code: PageCode;
	selectedTab: MySubmissionTabs;
}

export const MySubmissionsTabsView = ({
	code,
	selectedTab,
}: MyVerificationsTabsViewProps) => {
	const { t } = useTranslation([code]);
	const navigate = useNavigate();
	const loginInfo = useIdentity();
	const { extraConfig } = useExtraConfigContext();
	const { isTabVisible, tabLabel } = useMySubmissionTabs(
		extraConfig.submissionsRoutes.routesConfig
	);

	const getRouteFromIdentifier = (identifier: string) => {
		switch (identifier) {
			case MySubmissionTabs.osvedceni:
				return pageRouteResolver('moje-podani.osvedceni');
			case MySubmissionTabs.autorizace:
				return pageRouteResolver('moje-podani.autorizace');
			case MySubmissionTabs.podani:
				return pageRouteResolver('moje-podani');
			default:
				return '';
		}
	};

	const isTabSelected = useCallback(
		(tab: MySubmissionTabs) => (selectedTab === tab ? true : undefined),
		[selectedTab]
	);

	// in case user is loa low and is trying to access "osvedceni" -> switch him to "podani"
	if (loginInfo.isLowLoa && selectedTab === MySubmissionTabs.osvedceni) {
		navigate(getRouteFromIdentifier(MySubmissionTabs.podani));
		return;
	}

	return (
		<Tabs
			color="primary"
			type="text"
			orientation="horizontal"
			items={[
				isTabVisible(MySubmissionTabs.podani)
					? {
							label: t(tabLabel(MySubmissionTabs.podani)),
							triggerIdentifier: MySubmissionTabs.podani,
							isActive: isTabSelected(MySubmissionTabs.podani),
							children: (
								<MojePodaniSeznamContextProvider code={code}>
									<MySubmissionsList code={code} />
								</MojePodaniSeznamContextProvider>
							),
						}
					: null,
				isTabVisible(MySubmissionTabs.osvedceni)
					? {
							label: t(tabLabel(MySubmissionTabs.osvedceni)),
							triggerIdentifier: MySubmissionTabs.osvedceni,
							isActive: isTabSelected(MySubmissionTabs.osvedceni),
							children: (
								<MojeOsvedceniSeznamContextProvider code={code}>
									<MyVerificationsList code={code} />
								</MojeOsvedceniSeznamContextProvider>
							),
						}
					: null,
				isTabVisible(MySubmissionTabs.autorizace)
					? {
							label: t(tabLabel(MySubmissionTabs.autorizace)),
							triggerIdentifier: MySubmissionTabs.autorizace,
							isActive: isTabSelected(MySubmissionTabs.autorizace),
							children: (
								<MojeAutorizaceSeznamContextProvider code={code}>
									<MyAuthorizationsContentView />
								</MojeAutorizaceSeznamContextProvider>
							),
						}
					: null,
			].filter(is)}
			onChange={(event) => navigate(getRouteFromIdentifier(event))}
		/>
	);
};
